import { View, Text, styled, ScrollView } from 'tamagui'
import { useAppStore, useServerStore } from '@/store'
import { users, serverBilling } from '@/mocks'
import { roles, memberRoles } from '@/mocks/roles'
import { Flame } from '@tamagui/lucide-icons'
import { Avatar, ProBadge } from '../common'

const Sidebar = styled(View, {
  width: 240,
  backgroundColor: '$sidebar',
  flexDirection: 'column',
  flexShrink: 0,
  borderLeftWidth: 1,
  borderLeftColor: '$borderDefault',
})

const SidebarHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const HeaderTitle = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  fontSize: 13,
  color: '$sidebarMuted',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const RoleSection = styled(View, {
  paddingTop: 12,
})

const RoleLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  paddingHorizontal: 16,
  paddingBottom: 4,
})

const MemberRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  minHeight: 36,
  paddingHorizontal: 12,
  paddingVertical: 4,
  marginHorizontal: 8,
  borderRadius: '$borderRadiusMd',
  gap: 8,
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const MemberInfo = styled(View, {
  flex: 1,
  gap: 0,
})

const MemberName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
})

const ActivityText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  color: '$sidebarMuted',
  // @ts-expect-error -- web overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

const activityVerbs: Record<string, string> = {
  playing: 'Playing',
  listening: 'Listening to',
  watching: 'Watching',
  streaming: 'Streaming on',
  competing: 'Competing in',
}

export function MemberSidebar() {
  const { activeServerId, openProfileModal } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)

  if (!server || !activeServerId) return null

  const serverRoles = roles[activeServerId] || []
  const serverMembers = memberRoles[activeServerId] || {}

  // Group members by role
  const roleGroups: { roleId: string; roleName: string; roleColor: string; memberIds: string[] }[] = []

  for (const role of serverRoles) {
    const members = Object.entries(serverMembers)
      .filter(([_, rId]) => rId === role.id)
      .map(([userId]) => userId)
    if (members.length > 0) {
      roleGroups.push({
        roleId: role.id,
        roleName: role.name,
        roleColor: role.color,
        memberIds: members,
      })
    }
  }

  // Fuel contributors for this server
  const billing = serverBilling[activeServerId]
  const fuelContributorIds = new Set(billing?.fuelContributors.map(fc => fc.userId) || [])

  // Separate online/offline
  const onlineStatuses = new Set(['online', 'idle', 'dnd'])

  return (
    <Sidebar>
      <SidebarHeader>
        <HeaderTitle>Members — {Object.keys(serverMembers).length}</HeaderTitle>
      </SidebarHeader>
      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        {roleGroups.map((group) => {
          const onlineMembers = group.memberIds.filter(id => {
            const u = users[id]
            return u && onlineStatuses.has(u.status)
          })
          const offlineMembers = group.memberIds.filter(id => {
            const u = users[id]
            return u && !onlineStatuses.has(u.status)
          })

          return (
            <RoleSection key={group.roleId}>
              <RoleLabel color={group.roleColor}>
                {group.roleName} — {onlineMembers.length}
              </RoleLabel>
              {onlineMembers.map((userId) => {
                const user = users[userId]
                if (!user) return null
                return (
                  <MemberRow key={userId} onPress={() => openProfileModal(userId)}>
                    <Avatar
                      name={user.displayName}
                      color={user.roleColor || '#8B95A8'}
                      status={user.status}
                      size="sm"
                    />
                    <MemberInfo>
                      <MemberName color={group.roleColor}>{user.displayName}</MemberName>
                      {user.activity && (
                        <ActivityText>{activityVerbs[user.activity.type]} {user.activity.name}</ActivityText>
                      )}
                    </MemberInfo>
                    {fuelContributorIds.has(userId) && <Flame size={12} color="#E8A840" />}
                    {user.isPro && <ProBadge size="sm" />}
                  </MemberRow>
                )
              })}
              {offlineMembers.map((userId) => {
                const user = users[userId]
                if (!user) return null
                return (
                  <MemberRow key={userId} onPress={() => openProfileModal(userId)} opacity={0.5}>
                    <Avatar
                      name={user.displayName}
                      color={user.roleColor || '#8B95A8'}
                      status={user.status}
                      size="sm"
                    />
                    <MemberInfo>
                      <MemberName color="$sidebarMuted">{user.displayName}</MemberName>
                    </MemberInfo>
                    {fuelContributorIds.has(userId) && <Flame size={12} color="#E8A840" />}
                    {user.isPro && <ProBadge size="sm" />}
                  </MemberRow>
                )
              })}
            </RoleSection>
          )
        })}
      </ScrollView>
    </Sidebar>
  )
}
